import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ReplayIcon from '@mui/icons-material/Replay'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { AppDispatch } from '../../../app/store'
import {
  downloadDocumentFromDMSById,
  getDocumentIdFromHasura,
  postSpeechToText,
} from '../../../services/OpdFormsSTTService'
import {
  setIsInitialLoad,
  setLoading,
  updateClinicalExamination,
  updateDiagnosis,
  updateExamination,
  updateHistoryExamination,
  updateObjectivePhysio,
  updatePainHistoryPhysio,
  updateSystemicExamination,
  updateTreatmentPlan,
  updateVitals,
} from './opdFormsSlice'
import {
  AUDIO_TYPE,
  cleanNoRelevantDetailsMsgConstants,
  CLINICAL_ANALYSIS_KEYS,
  DIAGNOSIS_ANALYSIS_KEYS,
  EXAMINATION_ANALYSIS_KEYS,
  generateFilename,
  HISTORY_ANALYSIS_KEYS,
  MIC_FEATURE_CONST,
  noRelevantDetailsMsg,
  OBJECTIVE_ANALYSIS_KEYS,
  OPDFORM_ANALYSIS_KEYS,
  PAIN_HISTORY_ANALYSIS_KEYS,
  SYSTEMIC_EXAMINATION_ANALYSIS_KEYS,
  toastMessages,
  TREATMENTPLAN_ANALYSIS_KEYS,
  VITALS_ANALYSIS_KEYS,
} from './constants'
import { toast } from 'react-toastify'
import DownloadRecording from '../../../assets/icons/download.svg'

const styles = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  transcribeButton: {
    backgroundColor: '#4682b4!important',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'capitalize',
  },
  disabledTranscribe: {
    backgroundColor: '#BDBDBD !important',
    color: '#FFFFFF !important',
  },
  timer: {
    minWidth: '60px',
    textAlign: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    fontSize: '12px',
  },
  loader: {
    marginTop: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  result: {
    paddingTop: '1rem',
  },
  statusRow: {
    minHeight: '24px',
    marginLeft: '8px',
  },
  statusMessage: {
    fontStyle: 'bold',
    color: '#363463',
    fontFamily: 'Noto Sans,sans-serif',
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'center',
  },
})

export const cleanNoRelevantDetailsMsg = (obj: any): any => {
  if (Array?.isArray(obj)) {
    return obj?.map(cleanNoRelevantDetailsMsg)
  }

  if (
    obj !== null &&
    typeof obj === cleanNoRelevantDetailsMsgConstants.objectStr
  ) {
    const cleanedObj: Record<string, any> = {}
    for (const [key, value] of Object.entries(obj)) {
      cleanedObj[key] = cleanNoRelevantDetailsMsg(value)
    }
    return cleanedObj
  }

  if (
    typeof obj === cleanNoRelevantDetailsMsgConstants.stringStr &&
    obj?.trim() === noRelevantDetailsMsg
  ) {
    return ''
  }

  return obj
}

const MicFeature = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [apiLoading, setApiLoading] = useState(false)
  const [hasStopped, setHasStopped] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [timer, setTimer] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunks = useRef<Blob[]>([])
  const timerInterval = useRef<NodeJS.Timer | null>(null)
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const [fileName, setFileName] = useState('')
  const [fileNameDmsIdMap, setFileNameDmsIdMap] = useState<
    Record<string, string>
  >({})
  const visitId: any = id
  const startTimer = () => {
    if (timerInterval.current) clearInterval(timerInterval.current)
    timerInterval.current = setInterval(() => {
      setTimer((prev) => prev + 1)
    }, 1000)
  }

  const stopTimer = () => {
    if (timerInterval?.current) {
      clearInterval(timerInterval?.current)
      timerInterval.current = null
    }
  }

  const startRecording = async () => {
    const stream = await navigator?.mediaDevices?.getUserMedia({ audio: true })
    const mediaRecorder = new MediaRecorder(stream)
    mediaRecorderRef.current = mediaRecorder
    audioChunks.current = []

    mediaRecorder.ondataavailable = (event) => {
      audioChunks?.current?.push(event?.data)
    }

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: AUDIO_TYPE })
      setAudioBlob(audioBlob)
    }

    mediaRecorder?.start()
    setIsRecording(true)
    setHasStopped(false)
    setIsPaused(false)
    setTimer(0)
    startTimer()
  }

  const pauseRecording = () => {
    mediaRecorderRef?.current?.pause()
    setIsPaused(true)
    stopTimer()
  }

  const resumeRecording = () => {
    mediaRecorderRef.current?.resume()
    setIsPaused(false)
    startTimer()
  }

  const stopRecording = () => {
    mediaRecorderRef.current?.stop()
    setIsRecording(false)
    setIsPaused(false)
    setHasStopped(true)
    stopTimer()

    const fileName = `${generateFilename()}.${AUDIO_TYPE.split('/')[1]}`
    setFileName(fileName)
  }

  const reRecord = () => {
    startRecording()
    stopTimer()
    setIsRecording(false)
    setIsPaused(false)
    setHasStopped(false)
    setAudioBlob(null)
    setTimer(0)
  }

  const formatTimer = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const getStatusMessage = () => {
    if (isTranscribing) return MIC_FEATURE_CONST.recTranscribing
    if (isRecording && isPaused) return MIC_FEATURE_CONST.recPaused
    if (isRecording && !isPaused) return MIC_FEATURE_CONST.recInProgress
    if (hasStopped) return MIC_FEATURE_CONST.recStopped
    return ''
  }

  const handleTranscribe = async () => {
    if (!audioBlob || !visitId) return
    setIsTranscribing(true)
    dispatch(setLoading(true))
    await postFileToSpeechToTextService(audioBlob)
    dispatch(setLoading(false))
    setIsTranscribing(false)
  }

  const postFileToSpeechToTextService = async (blob: Blob) => {
    // Notify user if network connectivity is lost.
    if (!navigator.onLine) {
      toast.error(toastMessages.NetworkFailed)
    }
    setIsTranscribing(true)

    const dmsId = fileNameDmsIdMap?.[fileName] || ''
    const response = await postSpeechToText(blob, visitId, dmsId, fileName)

    if (response?.success) {
      dispatch(setIsInitialLoad(false))
    } else {
      toast.error(toastMessages.ServiceBroken)
    }
    setIsTranscribing(false)

    const analysis = cleanNoRelevantDetailsMsg(
      response?.data?.whisper_response?.data.Analysis
    )
    const dmsResponse = response?.data?.dms_response?.data

    if (dmsResponse?.id) {
      setFileNameDmsIdMap((prev) => ({ ...prev, [fileName]: dmsResponse?.id }))
    }

    const historyExaminationData =
      analysis?.[OPDFORM_ANALYSIS_KEYS?.HISTORY_AND_EXAMINATION]
    const vitalsData = analysis?.[OPDFORM_ANALYSIS_KEYS?.VITALS]
    const systemicExaminationData =
      analysis?.[OPDFORM_ANALYSIS_KEYS?.SYSTEMIC_EXAMINATION]
    const diagnosisData = analysis?.[OPDFORM_ANALYSIS_KEYS?.DIAGNOSIS]
    const objectiveData = analysis?.[OPDFORM_ANALYSIS_KEYS?.OBJECTIVE]
    const painHistoryData = analysis?.[OPDFORM_ANALYSIS_KEYS?.PAIN_HISTORY]
    const clinicalExaminationData = systemicExaminationData
    const examinationData = systemicExaminationData
    const treatmentPlanData = analysis?.[OPDFORM_ANALYSIS_KEYS?.TREATMENT_PLAN]

    // If no relevant details are present in the response, notify user.
    if (
      historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.CHIEF_COMPLAINTS] == '' &&
      historyExaminationData?.[
        HISTORY_ANALYSIS_KEYS?.HISTORY_OF_PRESENT_ILLNESS
      ] == '' &&
      historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.PAST_HISTORY] == '' &&
      historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.PAST_MEDICAL_HISTORY] ==
        ''
    ) {
      toast.error(toastMessages.NoRelevantDetailsError)
    }

    dispatch(
      updateHistoryExamination({
        chiefComplaints:
          historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.CHIEF_COMPLAINTS],
        historyOfPresentIllness:
          historyExaminationData?.[
            HISTORY_ANALYSIS_KEYS?.HISTORY_OF_PRESENT_ILLNESS
          ],
        pastHistory:
          historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.PAST_HISTORY],
        pastMedicalHistory:
          historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.PAST_MEDICAL_HISTORY],
        habitualHistory:
          historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.HABITUAL_HISTORY],
        obstetricHistory:
          historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.OBSTETRIC_HISTORY],
        familyHistory:
          historyExaminationData?.[HISTORY_ANALYSIS_KEYS?.FAMILY_HISTORY],
      })
    )

    dispatch(
      updateVitals({
        pulse: vitalsData?.[VITALS_ANALYSIS_KEYS?.PULSE],
        temperature: vitalsData?.[VITALS_ANALYSIS_KEYS?.TEMPERATURE],
        respiratoryRate: vitalsData?.[VITALS_ANALYSIS_KEYS?.RESPIRATORY_RATE],
        spo2: vitalsData?.[VITALS_ANALYSIS_KEYS?.SPO2],
        systolic:
          vitalsData?.[VITALS_ANALYSIS_KEYS?.BLOOD_PRESSURE]?.split('/')?.[0],
        diastolic:
          vitalsData?.[VITALS_ANALYSIS_KEYS?.BLOOD_PRESSURE]?.split('/')?.[1],
        posture: vitalsData?.[VITALS_ANALYSIS_KEYS?.POSTURE],
      })
    )

    dispatch(
      updateSystemicExamination({
        localExamination:
          systemicExaminationData?.[
            SYSTEMIC_EXAMINATION_ANALYSIS_KEYS?.LOCAL_EXAMINATION
          ],
        cardiovascularSystem:
          systemicExaminationData?.[
            SYSTEMIC_EXAMINATION_ANALYSIS_KEYS?.CARDIO_EXAMINATION
          ],
        respiratorySystem:
          systemicExaminationData?.[
            SYSTEMIC_EXAMINATION_ANALYSIS_KEYS?.RESPIRATORY_EXAMINATION
          ],
        nervousSystem:
          systemicExaminationData?.[
            SYSTEMIC_EXAMINATION_ANALYSIS_KEYS?.NERVOUS_SYSTEM
          ],
        perAbdomen:
          systemicExaminationData?.[
            SYSTEMIC_EXAMINATION_ANALYSIS_KEYS?.PER_ABDOMEN
          ],
      })
    )

    dispatch(
      updateDiagnosis({
        provisionalDiagnosis:
          diagnosisData?.[DIAGNOSIS_ANALYSIS_KEYS?.PROVISIONAL],
        differentialDiagnosis:
          diagnosisData?.[DIAGNOSIS_ANALYSIS_KEYS?.DIFFERENTIAL],
        investigations:
          diagnosisData?.[DIAGNOSIS_ANALYSIS_KEYS?.INVESTIGATIONS],
        finalDiagnosis: diagnosisData?.[DIAGNOSIS_ANALYSIS_KEYS?.FINAL],
      })
    )

    dispatch(
      updateObjectivePhysio({
        posture: objectiveData?.[OBJECTIVE_ANALYSIS_KEYS?.POSTURE],
        gait: objectiveData?.[OBJECTIVE_ANALYSIS_KEYS?.GAIT],
      })
    )

    dispatch(
      updatePainHistoryPhysio({
        onset: painHistoryData?.[PAIN_HISTORY_ANALYSIS_KEYS?.ONSET],
        whatRelievesPain:
          painHistoryData?.[PAIN_HISTORY_ANALYSIS_KEYS?.RELIEVES_PAIN],
        accompanyingSymptoms:
          painHistoryData?.[PAIN_HISTORY_ANALYSIS_KEYS?.ACCOMPANYING_SYMPTOMS],
      })
    )

    dispatch(
      updateClinicalExamination({
        intraOralExamination:
          clinicalExaminationData?.[CLINICAL_ANALYSIS_KEYS?.INTRA_ORAL],
        swelling: clinicalExaminationData?.[CLINICAL_ANALYSIS_KEYS?.SWELLING],
        lymphondeEnlargement:
          clinicalExaminationData?.[CLINICAL_ANALYSIS_KEYS?.LYMPHNODE],
        sinusOpening: clinicalExaminationData?.[CLINICAL_ANALYSIS_KEYS?.SINUS],
      })
    )

    dispatch(
      updateExamination({
        palpation: examinationData?.[EXAMINATION_ANALYSIS_KEYS?.PALPATION],
        reflexes: examinationData?.[EXAMINATION_ANALYSIS_KEYS?.REFLEXES],
        rom: examinationData?.[EXAMINATION_ANALYSIS_KEYS?.ROM],
        muscleAssessment:
          examinationData?.[EXAMINATION_ANALYSIS_KEYS?.MUSCLE_ASSESSMENT],
        jointAssessment:
          examinationData?.[EXAMINATION_ANALYSIS_KEYS?.JOINT_ASSESSMENT],
        relevantSpecialTests:
          examinationData?.[EXAMINATION_ANALYSIS_KEYS?.RELEVANT_TESTS],
      })
    )

    dispatch(
      updateTreatmentPlan({
        treatmentPlan:
          treatmentPlanData?.[TREATMENTPLAN_ANALYSIS_KEYS?.TREATMENT_PLAN],
        followUpAdvice:
          treatmentPlanData?.[TREATMENTPLAN_ANALYSIS_KEYS?.FOLLOW_UP],
        homeAdvice:
          treatmentPlanData?.[TREATMENTPLAN_ANALYSIS_KEYS?.HOME_ADVICE],
      })
    )
  }

  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current)
      }
    }
  }, [])
  const handleAudioFileDownloadClick = async () => {
    if (!visitId) {
      console.error('No encounter id found.')
    }
    setApiLoading(true)
    const hasuraResp = await getDocumentIdFromHasura(visitId)
    const documentIdsArray = hasuraResp?.data

    if (Array.isArray(documentIdsArray) && documentIdsArray.length > 0) {
      const latestDocumentId = hasuraResp?.data?.reduce(
        (latest: any, current: any) =>
          new Date(current.updated_at) > new Date(latest.updated_at)
            ? current
            : latest
      ).document_id

      // Download file from DMS
      if (latestDocumentId) {
        const dmsResp = await downloadDocumentFromDMSById(latestDocumentId)

        if (dmsResp?.success) {
          const filename = `${visitId}.webm`
          saveAudioFileToFileSystem(dmsResp?.data, filename)
          toast.success(`${filename} downloaded.`, { autoClose: 800 })
        } else {
          toast.error('Failed to save file.', { autoClose: 500 })
        }
      }
      setApiLoading(false)
    } else {
      setApiLoading(false)
      toast.error('No recording found.', { autoClose: 500 })
    }
  }
  const saveAudioFileToFileSystem = (response: any, filename: string) => {
    // Download and save the file
    const blob = new Blob([response?.data])
    const urlBlob = window.URL.createObjectURL(blob)
    const element = document.createElement('a')
    element.href = urlBlob
    element.download = filename
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
    window.URL.revokeObjectURL(urlBlob)
  }
  const isDisable = isRecording || isTranscribing || apiLoading
  return (
    <>
      <Box
        width={'fit-content'}
        padding={'unset'}
        borderRadius={2}
        boxShadow='0px 2px 10px rgba(0,0,0,0.1)'
        bgcolor='white'
      >
        <Box className={classes?.container}>
          <Box
            display='flex'
            alignItems='center'
            gap={2}
            width={'100%'}
            justifyContent={'space-between'}
          >
            {!isRecording && !hasStopped && (
              <IconButton onClick={startRecording}>
                <MicIcon color='primary' />
              </IconButton>
            )}
            <Typography className={classes?.timer}>
              {formatTimer(timer)}
            </Typography>
            <Tooltip
              arrow
              placement='top'
              title={
                <Box>
                  <Typography
                    variant='subtitle2'
                    fontWeight='bold'
                    gutterBottom
                  >
                    Recording Controls Guide
                  </Typography>

                  <Box
                    display='flex'
                    alignItems='center'
                    fontWeight='bold'
                    gap={1}
                    mb={0.5}
                  >
                    <PauseIcon fontSize='small' />
                    <Typography variant='body2'>
                      Pauses the recording
                    </Typography>
                  </Box>

                  <Box
                    display='flex'
                    alignItems='center'
                    fontWeight='bold'
                    gap={1}
                    mb={0.5}
                  >
                    <PlayArrowIcon fontSize='small' />
                    <Typography variant='body2'>
                      Resumes the recording
                    </Typography>
                  </Box>

                  <Box
                    display='flex'
                    alignItems='center'
                    fontWeight='bold'
                    gap={1}
                    mb={0.5}
                  >
                    <ReplayIcon fontSize='small' />
                    <Typography variant='body2'>
                      Discards the current recording and starts recording from
                      beginning
                    </Typography>
                  </Box>

                  <Box
                    display='flex'
                    alignItems='center'
                    fontWeight='bold'
                    gap={1}
                    mb={0.5}
                  >
                    <MicIcon fontSize='small' />
                    <Typography variant='body2'>
                      Starts the recording
                    </Typography>
                  </Box>

                  <Box
                    display='flex'
                    alignItems='center'
                    fontWeight='bold'
                    gap={1}
                    mb={0.5}
                  >
                    <StopIcon fontSize='small' color='error' />
                    <Typography variant='body2'>
                      Stops the recording and enables transcription
                    </Typography>
                  </Box>

                  <Box display='flex' alignItems='center' gap={1}>
                    <Button variant='contained' size='small' color='primary'>
                      Transcribe
                    </Button>
                    <Typography variant='body2'>
                      Sends recording for transcription
                    </Typography>
                  </Box>

                  <Box display='flex' alignItems='flex-start' gap={1} mb={0.5}>
                    <WarningAmberIcon color='warning' fontSize='small' />
                    <Typography variant='body2'>
                      Recording can only be sent for transcription after
                      clicking the Stop icon. Please do not click anywhere when
                      transcription is in progress.
                    </Typography>
                  </Box>
                </Box>
              }
            >
              <IconButton>
                <NotificationsNoneIcon color='action' />
              </IconButton>
            </Tooltip>
            <Box>
              <IconButton
                disabled={isDisable}
                onClick={handleAudioFileDownloadClick}
                className={isDisable ? classes?.disabledTranscribe : ''}
              >
                <img
                  title='Download Recording'
                  src={DownloadRecording}
                  alt='Download Recording'
                  width={'17px'}
                />
              </IconButton>
            </Box>
          </Box>

          <Box
            display='flex'
            alignItems='center'
            gap={1}
            flexWrap='wrap'
            mt={1}
          >
            {isRecording && (
              <>
                <IconButton onClick={stopRecording}>
                  <StopIcon color='error' />
                </IconButton>
                {isPaused ? (
                  <IconButton onClick={resumeRecording}>
                    <PlayArrowIcon />
                  </IconButton>
                ) : (
                  <IconButton onClick={pauseRecording}>
                    <PauseIcon />
                  </IconButton>
                )}
              </>
            )}

            {(isRecording || hasStopped) && (
              <>
                <IconButton onClick={reRecord}>
                  <ReplayIcon />
                </IconButton>

                {hasStopped && (
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    onClick={handleTranscribe}
                    disabled={isTranscribing}
                    className={`${classes?.transcribeButton} ${
                      isTranscribing ? classes?.disabledTranscribe : ''
                    }`}
                  >
                    Transcribe
                  </Button>
                )}
              </>
            )}
          </Box>

          <Box className={classes?.statusRow}>
            {(isRecording || isTranscribing || hasStopped) && (
              <Typography className={classes?.statusMessage}>
                {getStatusMessage()}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default MicFeature
